/**
 * TechSheetButton — Pulsante compatto "Scheda tecnica" con dropdown IT/EN.
 *
 * Usato in tutte le viste dove appare un prodotto (admin, agente, magazzino, ecc.):
 * apre il PDF della scheda tecnica in una nuova tab (con auth token).
 *
 * Props:
 *  - productId: string (obbligatorio)
 *  - variant: "icon" | "compact" | "full" — default "compact"
 *  - align: "left" | "right" — allineamento del menu
 *
 * Il pulsante NON è visibile ai clienti (viene mostrato solo se il rendering
 * avviene in un contesto admin/agent/warehouse — il componente in sé è agnostico,
 * l'ACL è a livello di endpoint).
 */
import React, { useState } from "react";
import { FileText, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { openAuthenticatedPdf } from "../utils/pdf";

export default function TechSheetButton({ productId, variant = "compact", align = "end", label = "Scheda" }) {
  const [busy, setBusy] = useState(false);

  const openPdf = (lang) => {
    setBusy(true);
    try {
      openAuthenticatedPdf(`/products/${productId}/tech-sheet/pdf?lang=${lang}`);
    } finally {
      setTimeout(() => setBusy(false), 500);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "icon" ? (
          <button
            type="button"
            className="p-1.5 rounded hover:bg-stone-100 text-stone-600 hover:text-[#8A1C2A] transition"
            title="Scheda tecnica"
            data-testid={`tech-sheet-btn-${productId}`}
          >
            <FileText className="w-4 h-4" />
          </button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            className="h-7 text-xs border-stone-300 text-stone-700 hover:bg-stone-50"
            data-testid={`tech-sheet-btn-${productId}`}
          >
            <FileText className="w-3.5 h-3.5 mr-1" />
            {label}
            <ChevronDown className="w-3 h-3 ml-0.5" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-40">
        <DropdownMenuItem onClick={() => openPdf("it")} data-testid={`tech-sheet-it-${productId}`}>
          🇮🇹 Italiano (PDF)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openPdf("en")} data-testid={`tech-sheet-en-${productId}`}>
          🇬🇧 English (PDF)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
